import React, { useState, useMemo } from 'react';
import { Users, UserCheck, UserX, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { EquipeIcon } from '@/components/equipe/EquipeIcon';
import MembreRotationCard from './MembreRotationCard';
import ModalRotation from './ModalRotation';
import AffectationModal from '@/components/calendar/AffectationModal';
import { ModalAffectationChantierBulk } from '@/components/equipe/ModalAffectationChantierBulk';
import { usePlanningStore } from '@/stores/planningStore';
import type { MembreDisponibilite } from '@/hooks/useDisponibilites';

// Type pour les membres groupés par équipe
interface EquipeGroupe {
  equipe_id: string | null;
  equipe_nom: string | null;
  equipe_pictogramme: string | null;
  equipe_couleur: string | null;
  membres: MembreDisponibilite[];
}

// Fonction pour grouper les membres par équipe
function grouperParEquipe(membres: MembreDisponibilite[]): EquipeGroupe[] {
  const groupes: Record<string, EquipeGroupe> = {};

  membres.forEach((membre) => {
    const key = membre.equipe_id || 'sans-equipe';
    if (!groupes[key]) {
      groupes[key] = {
        equipe_id: membre.equipe_id,
        equipe_nom: membre.equipe_nom || 'Sans équipe',
        equipe_pictogramme: membre.equipe_pictogramme,
        equipe_couleur: membre.equipe_couleur,
        membres: [],
      };
    }
    groupes[key].membres.push(membre);
  });

  // Trier : équipes avec nom d'abord, "Sans équipe" à la fin
  return Object.values(groupes).sort((a, b) => {
    if (!a.equipe_id) return 1;
    if (!b.equipe_id) return -1;
    return (a.equipe_nom || '').localeCompare(b.equipe_nom || '');
  });
}

interface TeamsRotationSectionProps {
  membresDisponibles: MembreDisponibilite[];
  membresAffectes: MembreDisponibilite[];
  chantiers: any[];
  entrepriseId: string;
  onRotationComplete?: () => void;
}

/**
 * Section "Équipes & Rotation" dans le panneau Planning
 * - Toggle entre "Affectés" et "Disponibles"
 * - Liste des membres avec actions (rotation / affecter)
 */
const TeamsRotationSection = ({
  membresDisponibles,
  membresAffectes,
  chantiers,
  entrepriseId,
  onRotationComplete,
}: TeamsRotationSectionProps) => {
  const [activeView, setActiveView] = useState<'affectes' | 'disponibles'>('affectes');
  const [isRotationModalOpen, setIsRotationModalOpen] = useState(false);
  const [isAffectationModalOpen, setIsAffectationModalOpen] = useState(false);
  const [selectedMembre, setSelectedMembre] = useState<MembreDisponibilite | null>(null);
  // État pour les équipes dépliées (par défaut toutes repliées)
  const [expandedEquipes, setExpandedEquipes] = useState<Set<string>>(new Set());

  // ═══ MULTI-SÉLECTION ═══
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  // Récupérer la date sélectionnée dans le planning
  const selectedDate = usePlanningStore((state) => state.selectedDate);

  // Toggle pour déplier/replier une équipe
  const toggleEquipe = (equipeId: string) => {
    setExpandedEquipes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(equipeId)) {
        newSet.delete(equipeId);
      } else {
        newSet.add(equipeId);
      }
      return newSet;
    });
  };

  // ═══ HANDLERS MULTI-SÉLECTION ═══

  // Toggle sélection d'un membre
  const handleSelectChange = (id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  // Toggle sélection de tous les membres d'une équipe
  const handleToggleEquipe = (membres: MembreDisponibilite[]) => {
    const membreIds = membres.map((m) => m.id);
    const allSelected = membreIds.every((id) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        membreIds.forEach((id) => next.delete(id));
      } else {
        membreIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Réinitialiser la sélection quand on change de vue
  const handleViewChange = (view: 'affectes' | 'disponibles') => {
    setActiveView(view);
    setSelectedIds(new Set()); // Reset sélection
  };

  // Ouvrir le modal bulk avec les membres sélectionnés
  const handleOpenBulkModal = () => {
    if (selectedIds.size > 0) {
      setIsBulkModalOpen(true);
    }
  };

  // Succès du modal bulk
  const handleBulkSuccess = () => {
    setSelectedIds(new Set()); // Reset sélection
    setIsBulkModalOpen(false);
    onRotationComplete?.();
  };

  // Membres sélectionnés pour le modal bulk (format adapté)
  const membresForBulkModal = useMemo(() => {
    return membresDisponibles
      .filter((m) => selectedIds.has(m.id))
      .map((m) => ({
        id: m.id,
        prenom: m.prenom,
        nom: m.nom,
        poste: m.poste,
        taux_horaire: m.taux_horaire,
        equipe_id: m.equipe_id || undefined,
      }));
  }, [membresDisponibles, selectedIds]);

  // Équipes pour le groupement dans le modal bulk
  const equipesForBulkModal = useMemo(() => {
    const equipesMap = new Map<string, { id: string; nom: string; couleur?: string; pictogramme?: string }>();
    membresDisponibles.forEach((m) => {
      if (m.equipe_id && !equipesMap.has(m.equipe_id)) {
        equipesMap.set(m.equipe_id, {
          id: m.equipe_id,
          nom: m.equipe_nom || 'Sans nom',
          couleur: m.equipe_couleur || undefined,
          pictogramme: m.equipe_pictogramme || undefined,
        });
      }
    });
    return Array.from(equipesMap.values());
  }, [membresDisponibles]);

  // Date pré-sélectionnée au format string (YYYY-MM-DD)
  const preSelectedDateStr = useMemo(() => {
    if (selectedDate) {
      return format(selectedDate, 'yyyy-MM-dd');
    }
    return undefined;
  }, [selectedDate]);

  // Handler rotation (membre affecté → autre chantier)
  const handleRotation = (membre: MembreDisponibilite) => {
    setSelectedMembre(membre);
    setIsRotationModalOpen(true);
  };

  // Handler affectation (membre disponible → chantier)
  const handleAffecter = (membre: MembreDisponibilite) => {
    setSelectedMembre(membre);
    setIsAffectationModalOpen(true);
  };

  // Fermer les modals
  const handleCloseRotation = () => {
    setIsRotationModalOpen(false);
    setSelectedMembre(null);
  };

  const handleCloseAffectation = () => {
    setIsAffectationModalOpen(false);
    setSelectedMembre(null);
  };

  // Success handlers
  const handleRotationSuccess = () => {
    onRotationComplete?.();
    handleCloseRotation();
  };

  const membres = activeView === 'affectes' ? membresAffectes : membresDisponibles;

  // Grouper les membres par équipe
  const membresGroupes = useMemo(() => grouperParEquipe(membres), [membres]);

  return (
    <div className="flex flex-col h-full">
      {/* ═══ HEADER ═══ */}
      <div className="px-6 py-4 border-t border-border/30 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/10 rounded-lg">
              <Users className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Équipes & Rotation</h3>
              <p className="text-sm text-muted-foreground">
                Gérez les affectations de vos membres
              </p>
            </div>
          </div>
        </div>

        {/* Toggle Affectés / Disponibles */}
        <div className="flex border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => handleViewChange('affectes')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors',
              activeView === 'affectes'
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted text-muted-foreground'
            )}
          >
            <UserCheck className="w-4 h-4" />
            Sur chantier ({membresAffectes.length})
          </button>
          <button
            onClick={() => handleViewChange('disponibles')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-l border-border',
              activeView === 'disponibles'
                ? 'bg-green-500 text-white'
                : 'hover:bg-muted text-muted-foreground'
            )}
          >
            <UserX className="w-4 h-4" />
            Disponibles ({membresDisponibles.length})
          </button>
        </div>

        {/* Bouton bulk (visible si ≥1 membre sélectionné dans "Disponibles") */}
        {activeView === 'disponibles' && selectedIds.size > 0 && (
          <Button
            onClick={handleOpenBulkModal}
            className="w-full mt-3 bg-primary hover:bg-primary/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Affecter ({selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''})
          </Button>
        )}
      </div>

      {/* ═══ LISTE DES MEMBRES (groupés par équipe) ═══ */}
      <ScrollArea className="flex-1 px-6">
        <div className="py-4 space-y-4">
          {membresGroupes.length > 0 ? (
            membresGroupes.map((groupe) => {
              // Clé unique pour chaque vue (affectés/disponibles) + équipe
              const equipeKey = `${activeView}-${groupe.equipe_id || 'sans-equipe'}`;
              const isExpanded = expandedEquipes.has(equipeKey);

              // Pour la multi-sélection (seulement dans l'onglet "disponibles")
              const showCheckbox = activeView === 'disponibles';
              const membreIds = groupe.membres.map((m) => m.id);
              const selectedCount = membreIds.filter((id) => selectedIds.has(id)).length;
              const allSelected = selectedCount === groupe.membres.length && groupe.membres.length > 0;
              const someSelected = selectedCount > 0 && selectedCount < groupe.membres.length;

              return (
                <div key={equipeKey} className="rounded-lg border border-border bg-card overflow-hidden">
                  {/* En-tête de l'équipe (cliquable) */}
                  <div className="flex items-center gap-2 py-3 px-3 hover:bg-muted/50 transition-colors group">
                    {/* Checkbox "tout sélectionner" pour l'équipe (seulement disponibles) */}
                    {showCheckbox && (
                      <Checkbox
                        checked={allSelected}
                        ref={(el) => {
                          if (el && someSelected) {
                            (el as any).indeterminate = true;
                          }
                        }}
                        onCheckedChange={() => handleToggleEquipe(groupe.membres)}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-shrink-0"
                      />
                    )}
                    <button
                      onClick={() => toggleEquipe(equipeKey)}
                      className="flex items-center gap-2 flex-1 cursor-pointer"
                    >
                      {/* Chevron pour indiquer l'état */}
                      <div className={cn(
                        "transition-transform duration-200",
                        isExpanded ? "rotate-90" : "rotate-0"
                      )}>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground" />
                      </div>
                      <EquipeIcon
                        pictogramme={groupe.equipe_pictogramme || 'Users'}
                        couleur={groupe.equipe_couleur || '#6b7280'}
                        size="sm"
                      />
                      <span className="font-medium text-sm text-foreground">
                        {groupe.equipe_nom}
                      </span>
                      <span className="text-xs font-medium bg-muted text-white px-2 py-0.5 rounded-full ml-auto">
                        {showCheckbox && selectedCount > 0
                          ? `${selectedCount}/${groupe.membres.length}`
                          : `${groupe.membres.length} membre${groupe.membres.length > 1 ? 's' : ''}`}
                      </span>
                    </button>
                  </div>
                  {/* Membres de l'équipe (avec animation) */}
                  <div
                    className={cn(
                      "overflow-hidden transition-all duration-200 ease-in-out border-t border-border",
                      isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0 border-t-0"
                    )}
                  >
                    <div className="space-y-2 p-3 bg-muted/20">
                      {groupe.membres.map((membre) => (
                        <MembreRotationCard
                          key={membre.id}
                          membre={membre}
                          onRotation={activeView === 'affectes' ? handleRotation : undefined}
                          onAffecter={activeView === 'disponibles' ? handleAffecter : undefined}
                          showCheckbox={showCheckbox}
                          isSelected={selectedIds.has(membre.id)}
                          onSelectChange={handleSelectChange}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            // État vide
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground font-medium mb-1">
                Aucun membre {activeView === 'affectes' ? 'sur chantier' : 'disponible'}
              </p>
              <p className="text-sm text-muted-foreground/70">
                {activeView === 'affectes'
                  ? 'Affectez des membres depuis l\'onglet "Disponibles"'
                  : 'Tous vos membres sont actuellement sur chantier'}
              </p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* ═══ MODAL ROTATION ═══ */}
      <ModalRotation
        isOpen={isRotationModalOpen}
        onClose={handleCloseRotation}
        membre={selectedMembre}
        chantiers={chantiers}
        entrepriseId={entrepriseId}
        onSuccess={handleRotationSuccess}
      />

      {/* ═══ MODAL AFFECTATION ═══ */}
      {isAffectationModalOpen && selectedMembre && (
        <AffectationModal
          isOpen={isAffectationModalOpen}
          onClose={handleCloseAffectation}
          affectation={null}
          defaultDate={new Date()}
          chantiers={chantiers}
          membres={[selectedMembre]}
          equipes={[]}
          defaultMembreId={selectedMembre.id}
          onSave={async (data) => {
            // La modal existante gère déjà la création
            onRotationComplete?.();
            handleCloseAffectation();
            return true;
          }}
          onUpdate={async () => true}
        />
      )}

      {/* ═══ MODAL AFFECTATION BULK ═══ */}
      <ModalAffectationChantierBulk
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        membres={membresForBulkModal}
        equipes={equipesForBulkModal}
        entrepriseId={entrepriseId}
        onSuccess={handleBulkSuccess}
        preSelectedDateDebut={preSelectedDateStr}
        preSelectedDateFin={preSelectedDateStr}
      />
    </div>
  );
};

export default TeamsRotationSection;
